<template>
  <div class="employee-deductions-page">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">{{ t('Deduções de Colaboradores') }}</h1>
      <p class="page-description">
        {{ t('Gerencie as deduções de colaboradores') }}
      </p>
    </div>

    <!-- Action Bar -->
    <div class="action-bar">
      <div class="actions">
        <Button
          data-testid="deductions-nova-deducao-btn"
          :label="t('Nova Dedução')"
          icon="pi pi-plus"
          @click="openNewModal"
        />
      </div>
    </div>

    <!-- Deductions Table (always visible) -->
    <EmployeeDeductionsTable
      :deductions="deductions"
      :can-edit="true"
      :can-delete="true"
      :is-loading="isLoading"
      @edit="handleEdit"
      @view="handleView"
      @delete="handleDelete"
      @cancel="handleCancel"
      @reopen="handleReopen"
      @refresh="loadDeductions"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { permissionsService } from '../../../services/permissionsService';
import { useEmployeeDeductions } from '../../../composables/useEmployeeDeductions';
import { useConfirmAction } from '../../../composables/useConfirmAction';
import EmployeeDeductionsTable from './EmployeeDeductionsTable.vue';

type Id = number | string;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DeductionRow = Record<string, any>;

const { t } = useI18n();
const { listDeductions, deleteDeduction, cancelDeduction, reopenDeduction, isLoading } = useEmployeeDeductions();
const { confirmAction } = useConfirmAction();

// State
const deductions = ref<DeductionRow[]>([]);

// Computed
const isManager = computed((): boolean => {
  return permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente']);
});

// Methods
const loadDeductions = async (): Promise<void> => {
  log('EmployeeDeductionsPage', 'loadDeductions', 'Loading deductions');

  try {
    const data = await listDeductions({});
    deductions.value = data as DeductionRow[];
    log('EmployeeDeductionsPage', 'loadDeductions', 'Loaded deductions', { count: data.length });
  } catch (err) {
    log('EmployeeDeductionsPage', 'loadDeductions', 'Error', err);
    eventBus.emit('globalMessage.error', {
      message: t('Erro ao carregar deduções'),
      duration: 5000
    });
  }
};

const openNewModal = (): void => {
  log('EmployeeDeductionsPage', 'openNewModal', 'Opening new modal');
  eventBus.emit('modal.employee-deductions.open', null);
};

const handleEdit = (deduction: DeductionRow): void => {
  log('EmployeeDeductionsPage', 'handleEdit', 'Editing deduction', deduction.id);
  eventBus.emit('modal.employee-deductions.open', deduction);
};

const handleView = (deduction: DeductionRow): void => {
  log('EmployeeDeductionsPage', 'handleView', 'Viewing deduction', deduction.id);
  eventBus.emit('modal.employee-deductions.open', deduction);
};

const handleCancel = (deduction: DeductionRow): void => {
  log('EmployeeDeductionsPage', 'handleCancel', 'Canceling deduction', deduction.id);

  confirmAction({
    message: t('Tem certeza que deseja cancelar esta dedução?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const result = await cancelDeduction(deduction.id);
      if (result) {
        eventBus.emit('globalMessage.success', {
          message: t('Dedução cancelada com sucesso'),
          duration: 3000
        });
        loadDeductions();
      } else {
        eventBus.emit('globalMessage.error', {
          message: t('Erro ao cancelar dedução'),
          duration: 5000
        });
      }
    }
  });
};

const handleReopen = (deduction: DeductionRow): void => {
  log('EmployeeDeductionsPage', 'handleReopen', 'Reopening deduction', deduction.id);

  confirmAction({
    message: t('Tem certeza que deseja reabrir esta dedução?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const result = await reopenDeduction(deduction.id);
      if (result) {
        eventBus.emit('globalMessage.success', {
          message: t('Dedução reaberta com sucesso'),
          duration: 3000
        });
        loadDeductions();
      } else {
        eventBus.emit('globalMessage.error', {
          message: t('Erro ao reabrir dedução'),
          duration: 5000
        });
      }
    }
  });
};

const handleDelete = (deduction: DeductionRow): void => {
  log('EmployeeDeductionsPage', 'handleDelete', 'Deleting deduction', deduction.id);

  confirmAction({
    message: t('Tem certeza que deseja excluir esta dedução?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const success = await deleteDeduction(deduction.id);
      if (success) {
        eventBus.emit('globalMessage.success', {
          message: t('Dedução excluída com sucesso'),
          duration: 3000
        });
        loadDeductions();
      } else {
        eventBus.emit('globalMessage.error', {
          message: t('Erro ao excluir dedução'),
          duration: 5000
        });
      }
    }
  });
};

// Lifecycle
onMounted(() => {
  loadDeductions();

  // Listen for refresh events
  eventBus.on('employee-deductions.refresh', loadDeductions);
  eventBus.on('employee-deductions.created', loadDeductions);
  eventBus.on('employee-deductions.updated', loadDeductions);
});

onBeforeUnmount(() => {
  eventBus.off('employee-deductions.refresh', loadDeductions);
  eventBus.off('employee-deductions.created', loadDeductions);
  eventBus.off('employee-deductions.updated', loadDeductions);
});
</script>

<style scoped>
.employee-deductions-page {
  padding: 1.5rem;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 0.5rem 0;
}

.page-description {
  color: var(--text-color-secondary);
  margin: 0;
}

.action-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
}
</style>
